import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import JSZip from 'jszip';
import './migration.scss';
import SharedLogsPanel from './SharedLogsPanel';
import StaleBanner from '../../components/stale-session-banner';
const intelogLogo = require('../../assets/icons/white_logo_png.png');

// ── Log config ────────────────────────────────────────────────────────────────
const ts = () => { const d = new Date(); return d.toTimeString().slice(0,8)+'.'+String(d.getMilliseconds()).padStart(3,'0'); };
const authHeader = () => ({ 'x-access-token': localStorage.getItem('token') || '' });

// ── Syntax highlight ──────────────────────────────────────────────────────────
const esc = s => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
function hl(code, lang) {
  let h = esc(code);
  if (lang === 'csharp') {
    h = h
      .replace(/\b(using|namespace|public|private|protected|internal|static|readonly|class|record|interface|abstract|sealed|partial|override|virtual|async|await|return|new|var|void|string|int|long|bool|double|decimal|object|null|true|false|if|else|foreach|for|while|switch|case|break|throw|try|catch|finally|in|is|as|params)\b/g,'<span style="color:#c678dd">$1</span>')
      .replace(/("(?:[^"\\]|\\.)*")/g,'<span style="color:#98c379">$1</span>')
      .replace(/(\/\/[^\n]*)/g,'<span style="color:#5c6370;font-style:italic">$1</span>')
      .replace(/\b(\d+\.?\d*)\b/g,'<span style="color:#d19a66">$1</span>')
      .replace(/\[([A-Za-z]+(?:Attribute)?)\]/g,'<span style="color:#e5c07b">[$1]</span>');
  } else if (lang === 'json') {
    h = h
      .replace(/("(?:[^"\\]|\\.)*")(\s*:)/g,'<span style="color:#e06c75">$1</span>$2')
      .replace(/:(\s*)("(?:[^"\\]|\\.)*")/g,':$1<span style="color:#98c379">$2</span>')
      .replace(/\b(true|false|null)\b/g,'<span style="color:#c678dd">$1</span>')
      .replace(/\b(\d+\.?\d*)\b/g,'<span style="color:#d19a66">$1</span>');
  } else if (lang === 'xml') {
    h = h
      .replace(/(&lt;\/?[a-zA-Z][a-zA-Z0-9:.-]*)/g,'<span style="color:#e06c75">$1</span>')
      .replace(/([a-zA-Z-:]+)=/g,'<span style="color:#d19a66">$1</span>=')
      .replace(/("(?:[^"\\]|\\.)*")/g,'<span style="color:#98c379">$1</span>');
  } else if (lang === 'javascript') {
    h = h
      .replace(/\b(const|let|var|function|async|await|return|require|module|exports|class|new|if|else|for|while|try|catch|throw|import|export|default|from|of|in)\b/g,'<span style="color:#c678dd">$1</span>')
      .replace(/("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)/g,'<span style="color:#98c379">$1</span>')
      .replace(/(\/\/[^\n]*)/g,'<span style="color:#5c6370;font-style:italic">$1</span>');
  } else if (lang === 'markdown') {
    h = h
      .replace(/^(#{1,3} .+)$/gm,'<span style="color:#e06c75;font-weight:600">$1</span>')
      .replace(/\*\*(.+?)\*\*/g,'<strong style="color:#abb2bf">$1</strong>');
  }
  return h;
}
function fileLang(p) {
  if (p.endsWith('.cs')) return 'csharp';
  if (p.endsWith('.json')) return 'json';
  if (p.endsWith('.xml')||p.endsWith('.csproj')) return 'xml';
  if (p.endsWith('.md')) return 'markdown';
  if (p.endsWith('.js')||p.endsWith('.ts')) return 'javascript';
  return 'text';
}
function fileIcon(p) {
  if (p.endsWith('.cs')) return '◈';
  if (p.endsWith('.json')) return '{}';
  if (p.endsWith('.csproj')) return '▣';
  if (p.endsWith('.md')) return '≡';
  if (p.endsWith('.xml')) return '<>';
  if (p.endsWith('.js')||p.endsWith('.ts')) return 'JS';
  return '·';
}
function isMuleCore(p) {
  return p.includes('/mule/')||p.includes('src/main/mule')||p.includes('langgraph/')||
    p.includes('/nodes/')||p.includes('/helpers/')||p.includes('/tools/')||
    p.includes('/prompts/')||p.includes('/schemas/')||p.includes('/services/')||
    (p.endsWith('.xml')&&!p.includes('pom')&&!p.includes('log4j'))||
    ['graph.js','state.js','runner.js','index.js'].some(n=>p.endsWith(n));
}

// ── Parse helpers ─────────────────────────────────────────────────────────────
function parseFiles(text) {
  const out = {};
  const re = /```file:([^\n]+)\n([\s\S]*?)```/g;
  let m;
  while ((m = re.exec(text)) !== null) out[m[1].trim()] = m[2];
  return out;
}
function parseParts(text) {
  const parts = [];
  const re = /```(?:file:([^\n]+)|(\w*))\n([\s\S]*?)```/g;
  let cur = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > cur) parts.push({ type: 'text', content: text.slice(cur, m.index) });
    if (m[1]) parts.push({ type: 'file', path: m[1].trim(), code: m[3], lang: fileLang(m[1].trim()) });
    else parts.push({ type: 'code', lang: m[2]||'text', code: m[3] });
    cur = m.index + m[0].length;
  }
  if (cur < text.length) parts.push({ type: 'text', content: text.slice(cur) });
  return parts;
}

// ── System prompts ────────────────────────────────────────────────────────────
const CHAT_SYSTEM_PROMPT = `You are an expert MuleSoft 4 to .NET 8 migration agent and .NET architect.
You help developers with reviewing generated .NET code, answering questions about the migration, explaining MuleSoft to .NET mappings, fixing specific files, and general .NET 8 / C# questions.
When asked to generate or fix a specific file, output it using this format:
\`\`\`file:path/to/File.cs
// content
\`\`\`
Be concise and direct. Only trigger a full migration when the user explicitly asks to migrate or generate the project.`;

const SYSTEM_PROMPT = `You are an expert MuleSoft 4 to .NET 8 migration agent.
Analyze the uploaded MuleSoft project and generate a complete, compilable .NET 8 Web API.
OUTPUT FORMAT — every file must use this exact format:
\`\`\`file:path/to/File.cs
// content
\`\`\`
MuleSoft → .NET mapping:
- http:listener → [HttpGet/Post/Put/Delete] Controller action
- db:select/insert/update/delete → async Dapper/EF Core repository
- ee:transform DataWeave → LINQ / C# mapping
- choice/when/otherwise → if/else with HTTP 200/404/400
- set-payload → return Ok() / NotFound() / BadRequest()
- db:config → appsettings.json ConnectionStrings + DbContext
- error-handler → ErrorHandlingMiddleware
Required files: {ProjectName}.csproj, Program.cs, appsettings.json, Controllers/, Models/, Data/Repositories/, Middleware/ErrorHandlingMiddleware.cs, README.md
Standards: async/await, IActionResult, XML doc comments, ILogger<T>, Swagger, record DTOs.`;

function isMigrationIntent(text) {
  const t = text.toLowerCase();
  return [
    'migrate this','start migration','run migration','begin migration',
    'migrate the project','migrate project','convert this project',
    'generate the .net','generate dotnet project','create the .net project',
    'build the .net project','port this to .net','do the migration',
    'perform the migration','execute migration'
  ].some(w => t.includes(w));
}

// ── Sub-components ────────────────────────────────────────────────────────────
function Spinner() {
  const [f, setF] = useState(0);
  useEffect(() => { const id = setInterval(() => setF(x=>(x+1)%4), 280); return ()=>clearInterval(id); }, []);
  return <span style={{ fontFamily: 'monospace' }}>{['⠋','⠙','⠸','⠴'][f]}</span>;
}

function Prose({ content }) {
  const html = content
    .replace(/\*\*(.+?)\*\*/g,"<strong>$1</strong>")
    .replace(/`([^`\n]+)`/g,"<code>$1</code>")
    .replace(/^### (.+)$/gm,"<div style='color:#93c5fd;font-weight:500;font-size:13px;margin:8px 0 3px'>$1</div>")
    .replace(/^## (.+)$/gm,"<div style='color:#c4b5fd;font-weight:500;font-size:14px;margin:10px 0 4px'>$1</div>")
    .replace(/^[-*] (.+)$/gm,"<div style='display:flex;gap:6px;margin:1px 0'><span style='color:#3b82f6;margin-top:2px;font-size:10px'>▸</span><span>$1</span></div>")
    .replace(/\n/g,'<br/>');
  return <div className="ma-prose" dangerouslySetInnerHTML={{ __html: html }} />;
}

function CodeBlock({ code, lang, path, onView, isNew }) {
  const [cp, setCp] = useState(false);
  return (
    <div className={`ma-code-block${isNew?' ma-new-file':''}`}>
      <div className="ma-code-header">
        <div className="ma-code-header-left">
          {isNew && <span className="ma-new-dot" />}
          <span className="ma-code-label">{path ? `${fileIcon(path)} ${path}` : lang}</span>
          <span className="ma-code-lines">{code.split('\n').length}L</span>
        </div>
        <div className="ma-code-actions">
          {path && onView && <button className="ma-code-btn ma-open" onClick={()=>onView(path)}>open</button>}
          <button className={`ma-code-btn${cp?' ma-copied':''}`} onClick={()=>{navigator.clipboard.writeText(code);setCp(true);setTimeout(()=>setCp(false),1800);}}>
            {cp ? '✓' : 'copy'}
          </button>
        </div>
      </div>
      <pre className="ma-code-pre" dangerouslySetInnerHTML={{ __html: hl(code, lang) }} />
    </div>
  );
}

function categorizeFiles(files) {
  const cats = { langgraph:[], nodes:[], helpers:[], tools:[], prompts:[], schemas:[], services:[], root:[] };
  Object.keys(files).forEach(p => {
    const rel = p.replace(/^[^/]+\//, '');
    if (rel.startsWith('langgraph/')) cats.langgraph.push(p);
    else if (rel.startsWith('nodes/')) cats.nodes.push(p);
    else if (rel.startsWith('helpers/')) cats.helpers.push(p);
    else if (rel.startsWith('tools/')) cats.tools.push(p);
    else if (rel.startsWith('prompts/')) cats.prompts.push(p);
    else if (rel.startsWith('schemas/')) cats.schemas.push(p);
    else if (rel.startsWith('services/')) cats.services.push(p);
    else cats.root.push(p);
  });
  return cats;
}

// Build a nested tree object from flat { 'a/b/c.xml': content } paths
function buildTree(paths) {
  const root = {};
  paths.forEach(p => {
    const parts = p.split('/');
    let node = root;
    parts.forEach((part, i) => {
      if (i === parts.length - 1) {
        node[part] = { __file: true, __path: p };
      } else {
        node[part] = node[part] || {};
        node = node[part];
      }
    });
  });
  return root;
}

function TreeNode({ name, node, depth, selected, onSelect, accent, defaultOpen }) {
  const isFile = node.__file;
  const [open, setOpen] = useState(defaultOpen !== false);
  if (isFile) {
    return (
      <button
        className={`ma-tree-item${selected === node.__path ? ' active' : ''}`}
        style={{ paddingLeft: 8 + depth * 14 }}
        onClick={() => onSelect(node.__path)}
      >
        <span style={{ color: accent || 'rgba(99,120,150,0.6)', fontSize: 9, fontFamily: 'monospace', flexShrink: 0 }}>{fileIcon(name)}</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      </button>
    );
  }
  const children = Object.entries(node).sort(([an, av], [bn, bv]) => {
    const aIsFile = av.__file, bIsFile = bv.__file;
    if (aIsFile !== bIsFile) return aIsFile ? 1 : -1; // folders first
    return an.localeCompare(bn);
  });
  return (
    <div>
      <button
        className="ma-tree-folder"
        style={{ paddingLeft: 8 + depth * 14 }}
        onClick={() => setOpen(p => !p)}
      >
        <span className="ma-tree-folder-arrow" style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
        <span className="ma-tree-folder-icon">📁</span>
        <span className="ma-tree-folder-name">{name}</span>
      </button>
      {open && (
        <div>
          {children.map(([childName, childNode]) => (
            <TreeNode key={childName} name={childName} node={childNode} depth={depth + 1}
              selected={selected} onSelect={onSelect} accent={accent} defaultOpen={depth < 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function FileTree({ files, selected, onSelect, accent, emptyText }) {
  const paths = Object.keys(files).sort();
  if (!paths.length) return <div className="ma-tree-empty">{emptyText || 'No files'}</div>;
  const tree = buildTree(paths);
  const entries = Object.entries(tree).sort(([an, av], [bn, bv]) => {
    const aIsFile = av.__file, bIsFile = bv.__file;
    if (aIsFile !== bIsFile) return aIsFile ? 1 : -1;
    return an.localeCompare(bn);
  });
  return (
    <div className="ma-file-tree">
      <div className="ma-tree-root-label">ROOT</div>
      {entries.map(([name, node]) => (
        <TreeNode key={name} name={name} node={node} depth={0}
          selected={selected} onSelect={onSelect} accent={accent} defaultOpen={true} />
      ))}
    </div>
  );
}

function DropZone({ onFiles, disabled, accept = '.zip,.jar', idleText = 'Drop MuleSoft .zip or .jar' }) {
  const [drag, setDrag] = useState(false);
  const ref = useRef();
  const exts = accept.split(',').map(s => s.trim().toLowerCase());
  const accepted = name => exts.some(e => name.toLowerCase().endsWith(e));
  return (
    <div className={`ma-upload-zone${drag?' drag':''}`}
      onDragOver={e=>{e.preventDefault();setDrag(true);}}
      onDragLeave={()=>setDrag(false)}
      onDrop={e=>{e.preventDefault();setDrag(false);const f=[...e.dataTransfer.files].filter(x=>accepted(x.name));if(f.length)onFiles(f);}}
      onClick={()=>!disabled&&ref.current.click()}>
      <input ref={ref} type="file" accept={accept} multiple style={{display:'none'}} onChange={e=>onFiles([...e.target.files])} />
      <div className="ma-upload-zone-icon">⬆</div>
      <div className="ma-upload-zone-text">{drag ? 'Drop to upload' : idleText}</div>
      <div className="ma-upload-zone-sub">or click to browse</div>
    </div>
  );
}


// ── Git Source Panel ──────────────────────────────────────────────────────────
function GitSourcePanel({ backendDomain, onFiles, onName, disabled, log }) {
  const [provider, setProvider]           = useState('github');
  const [githubAccounts, setGithubAccounts] = useState([]);
  const [azureAccounts,  setAzureAccounts]  = useState([]);
  const [selectedGhId,   setSelectedGhId]   = useState('');
  const [selectedAzId,   setSelectedAzId]   = useState('');
  const [repos,          setRepos]          = useState([]);
  const [selectedRepo,   setSelectedRepo]   = useState('');
  const [branches,       setBranches]       = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [azProject,      setAzProject]      = useState('');
  const [azRepo,         setAzRepo]         = useState('');
  const [fetching,       setFetching]       = useState(false);
  const [fileCount,      setFileCount]      = useState(0);

  // Load Connected Apps accounts on mount
  useEffect(() => {
    console.log('[GitSourcePanel] fetching accounts from', backendDomain);
    fetch(`${backendDomain}/api/v1/repo`, { headers:{'Content-Type':'application/json',...authHeader()} })
      .then(r=>{ console.log('[GitSourcePanel] /api/v1/repo status', r.status); return r.json(); })
      .then(d=>{ console.log('[GitSourcePanel] /api/v1/repo data', d); setGithubAccounts(Array.isArray(d) ? d : (d.gitRepo||[])); })
      .catch(e=>console.error('[GitSourcePanel] /api/v1/repo error', e));
    fetch(`${backendDomain}/api/v1/azaccount`, { headers:{'Content-Type':'application/json',...authHeader()} })
      .then(r=>{ console.log('[GitSourcePanel] /api/v1/azaccount status', r.status); return r.json(); })
      .then(d=>{ console.log('[GitSourcePanel] /api/v1/azaccount data', d); setAzureAccounts(Array.isArray(d) ? d : (d.azaccount||[])); })
      .catch(e=>console.error('[GitSourcePanel] /api/v1/azaccount error', e));
  }, [backendDomain]);

  const ghAccount = githubAccounts.find(a => a._id === selectedGhId);
  const azAccount = azureAccounts.find(a => a._id === selectedAzId);

  // Load repos when GitHub account changes
  useEffect(() => {
    if (!ghAccount) { setRepos([]); setSelectedRepo(''); setBranches([]); setSelectedBranch(''); return; }
    console.log('[GitSourcePanel] fetching repos for', ghAccount.gitUsername);
    fetch(`${backendDomain}/api/v1/github/repos?username=${encodeURIComponent(ghAccount.gitUsername)}&pat=${encodeURIComponent(ghAccount.gitPat)}`,
      { headers: authHeader() })
      .then(r=>{ console.log('[GitSourcePanel] /github/repos status', r.status); return r.json(); })
      .then(data => { console.log('[GitSourcePanel] repos data', data); setRepos(Array.isArray(data)?data:[]); setSelectedRepo(''); setBranches([]); setSelectedBranch(''); })
      .catch(e=>console.error('[GitSourcePanel] /github/repos error', e));
  }, [selectedGhId, backendDomain]); // eslint-disable-line

  // Load branches when GitHub repo changes
  useEffect(() => {
    if (!ghAccount || !selectedRepo) { setBranches([]); setSelectedBranch(''); return; }
    fetch(`${backendDomain}/api/v1/github/branches?username=${encodeURIComponent(ghAccount.gitUsername)}&pat=${encodeURIComponent(ghAccount.gitPat)}&repo=${encodeURIComponent(selectedRepo)}`,
      { headers: authHeader() })
      .then(r=>r.json()).then(data => {
        const list = Array.isArray(data) ? data : [];
        setBranches(list);
        if (list.includes('main')) setSelectedBranch('main');
        else if (list.length > 0) setSelectedBranch(list[0]);
      }).catch(()=>{});
  }, [selectedRepo, backendDomain]); // eslint-disable-line

  const canFetch = provider==='github'
    ? !!(ghAccount && selectedRepo && selectedBranch)
    : !!(azAccount && azProject && azRepo);

  const handleFetch = async () => {
    setFetching(true); setFileCount(0);
    const body = provider==='github'
      ? { provider:'github', token:ghAccount.gitPat, repo:{ owner:ghAccount.gitUsername, name:selectedRepo }, branch:selectedBranch }
      : { provider:'azure',  token:azAccount.azDevopsPat, repo:{ org:azAccount.organization, project:azProject, name:azRepo }, branch:'main' };
    const repoName = provider==='github' ? selectedRepo : azRepo;
    log('info', `Connecting to ${provider==='github'?'GitHub':'Azure DevOps'}…`);
    try {
      const resp = await fetch(`${backendDomain}/api/v1/github/fetch`, {
        method:'POST', headers:{'Content-Type':'application/json',...authHeader()},
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(`Backend ${resp.status}`);
      const reader=resp.body.getReader(), decoder=new TextDecoder();
      let buffer='';
      while (true) {
        const {done,value}=await reader.read();
        if (done) break;
        buffer += decoder.decode(value,{stream:true});
        const blocks=buffer.split('\n\n'); buffer=blocks.pop();
        for (const block of blocks) {
          let eventType='message', dataStr='';
          for (const line of block.split('\n')) {
            if (line.startsWith('event: ')) eventType=line.slice(7).trim();
            else if (line.startsWith('data: ')) dataStr=line.slice(6);
          }
          if (!dataStr) continue;
          try {
            const ev=JSON.parse(dataStr);
            if (eventType==='status') { log('info',ev.msg); }
            else if (eventType==='file') {
              setFileCount(ev.fetched);
              if (ev.fetched%5===0||ev.fetched===ev.total) log('info',`${ev.fetched}/${ev.total} files fetched…`);
            } else if (eventType==='done') {
              log('success',ev.msg);
              const filesMap={};
              (ev.files||[]).forEach(f=>{ filesMap[f.path]=f.content; });
              onFiles(filesMap); onName(repoName);
            } else if (eventType==='error') {
              log('error',ev.msg);
            }
          } catch(_) {}
        }
      }
    } catch(e) { log('error',`Git fetch failed: ${e.message}`); }
    finally { setFetching(false); }
  };

  const selStyle = { width:'100%',background:'#0d1117',color:'#f0f4f8',border:'1px solid rgba(99,120,150,0.25)',borderRadius:7,padding:'7px 10px',fontSize:12,outline:'none',boxSizing:'border-box' };
  const optStyle = { background:'#0d1117',color:'#f0f4f8' };
  const inpStyle = { ...selStyle, color:'#f0f4f8' };

  return (
    <div style={{padding:'10px 12px',display:'flex',flexDirection:'column',gap:10,overflowY:'auto',maxHeight:'calc(100vh - 220px)'}}>
      {/* Provider tabs */}
      <div style={{display:'flex',background:'#080c12',border:'1px solid rgba(99,120,150,0.15)',borderRadius:8,overflow:'hidden'}}>
        {[['github','⬡ GitHub'],['azure','⬡ Azure DevOps']].map(([id,label])=>(
          <button key={id} onClick={()=>{setProvider(id);setFileCount(0);setRepos([]);setSelectedRepo('');setBranches([]);setSelectedBranch('');}}
            style={{flex:1,padding:'8px',background:provider===id?'#1e2530':'transparent',border:'none',borderBottom:provider===id?'2px solid #3b82f6':'2px solid transparent',color:provider===id?'#f0f4f8':'rgba(99,120,150,0.6)',fontSize:11,fontWeight:500,cursor:'pointer'}}>
            {label}
          </button>
        ))}
      </div>

      {/* GitHub fields */}
      {provider==='github' && <>
        <select value={selectedGhId} onChange={e=>setSelectedGhId(e.target.value)} style={selStyle}>
          <option value="" style={optStyle}>— Select GitHub account —</option>
          {githubAccounts.map(a=><option key={a._id} value={a._id} style={optStyle}>{a.gitUsername}</option>)}
        </select>
        {githubAccounts.length===0 && <div style={{fontSize:11,color:'#f59e0b',fontFamily:'monospace'}}>⚠ No GitHub accounts. Add one in Connected Apps.</div>}
        {ghAccount && (
          <select value={selectedRepo} onChange={e=>setSelectedRepo(e.target.value)} style={selStyle}>
            <option value="" style={optStyle}>{repos.length?'— Select repo —':'Loading repos…'}</option>
            {repos.map(r=><option key={r.name} value={r.name} style={optStyle}>{r.name}</option>)}
          </select>
        )}
        {selectedRepo && (
          <select value={selectedBranch} onChange={e=>setSelectedBranch(e.target.value)} style={selStyle}>
            <option value="" style={optStyle}>{branches.length?'— Select branch —':'Loading branches…'}</option>
            {branches.map(b=><option key={b} value={b} style={optStyle}>{b}</option>)}
          </select>
        )}
      </>}

      {/* Azure fields */}
      {provider==='azure' && <>
        <select value={selectedAzId} onChange={e=>setSelectedAzId(e.target.value)} style={selStyle}>
          <option value="" style={optStyle}>— Select Azure account —</option>
          {azureAccounts.map(a=><option key={a._id} value={a._id} style={optStyle}>{a.organization}</option>)}
        </select>
        {azureAccounts.length===0 && <div style={{fontSize:11,color:'#f59e0b',fontFamily:'monospace'}}>⚠ No Azure accounts. Add one in Connected Apps.</div>}
        {azAccount && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <input value={azProject} onChange={e=>setAzProject(e.target.value)} placeholder="Project name" style={inpStyle}/>
            <input value={azRepo}    onChange={e=>setAzRepo(e.target.value)}    placeholder="Repository name" style={inpStyle}/>
          </div>
        )}
      </>}

      {/* Fetch button */}
      <button onClick={handleFetch} disabled={!canFetch||fetching||disabled}
        style={{padding:'8px 14px',borderRadius:7,border:'none',fontSize:12,fontWeight:600,cursor:canFetch&&!fetching&&!disabled?'pointer':'not-allowed',background:canFetch&&!fetching&&!disabled?'#3b82f6':'#252d3a',color:canFetch&&!fetching&&!disabled?'#fff':'#4a5568',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
        {fetching?<><Spinner/> Fetching… {fileCount>0?`(${fileCount} files)`:''}</>:'⬇ Fetch MuleSoft Files'}
      </button>

    </div>
  );
}

// ── Push Panel ────────────────────────────────────────────────────────────────
function PushPanel({ files, uploadedName, onClose, backendDomain, targetLabel = '.NET 8' }) {
  const fileCount = Object.keys(files).length;
  const [provider, setProvider] = useState('github');
  const [cfg, setCfg] = useState({ repo:'', branch:'main', commitMessage:'', description:'', visibility:'private', project:'' });
  const [repoStatus, setRepoStatus] = useState(null);
  const [mode, setMode] = useState('auto');
  const [pushing, setPushing] = useState(false);
  const [pushLogs, setPushLogs] = useState([]);
  const [done, setDone] = useState(null);
  const [githubAccounts, setGithubAccounts] = useState([]);
  const [azureAccounts,  setAzureAccounts]  = useState([]);
  const [selectedGhId,   setSelectedGhId]   = useState('');
  const [selectedAzId,   setSelectedAzId]   = useState('');
  const logEndRef = useRef();
  const checkTimer = useRef(null);

  useEffect(() => {
    fetch(`${backendDomain}/api/v1/repo`, { headers:{'Content-Type':'application/json',...authHeader()} })
      .then(r=>r.json()).then(d=>{ setGithubAccounts(d.gitRepo||[]); if((d.gitRepo||[]).length===1) setSelectedGhId(d.gitRepo[0]._id); }).catch(()=>{});
    fetch(`${backendDomain}/api/v1/azaccount`, { headers:{'Content-Type':'application/json',...authHeader()} })
      .then(r=>r.json()).then(d=>{ setAzureAccounts(d.azaccount||[]); if((d.azaccount||[]).length===1) setSelectedAzId(d.azaccount[0]._id); }).catch(()=>{});
  }, [backendDomain]);

  useEffect(() => { if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior:'smooth' }); }, [pushLogs]);

  const set = (k,v) => setCfg(p => ({...p,[k]:v}));
  const defaultMsg = `chore: migrate ${uploadedName||'project'} to ${targetLabel}`;
  const addPushLog = (level,msg) => setPushLogs(p => [...p,{level,msg,time:ts()}]);

  const ghAccount = githubAccounts.find(a=>a._id===selectedGhId);
  const azAccount = azureAccounts.find(a=>a._id===selectedAzId);

  const canCheck = provider==='github'
    ? selectedGhId && cfg.repo
    : selectedAzId && cfg.project && cfg.repo;

  useEffect(() => {
    if (!canCheck) { setRepoStatus(null); return; }
    setRepoStatus({ checking: true });
    clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(async () => {
      try {
        const config = provider==='github'
          ? { token:ghAccount?.gitPat, owner:ghAccount?.gitUsername, repo:cfg.repo }
          : { token:azAccount?.azDevopsPat, org:azAccount?.organization, project:cfg.project, repo:cfg.repo };
        const r = await fetch(`${backendDomain}/api/v1/migration/mule-repo-check`, {
          method:'POST', headers:{'Content-Type':'application/json', ...authHeader()},
          body: JSON.stringify({ provider, config })
        });
        const d = await r.json();
        if (d.error) { setRepoStatus({ error: d.error }); return; }
        setRepoStatus(d);
        if (mode === 'auto') setMode(d.exists ? 'push' : 'create');
        if (d.exists && d.defaultBranch && !cfg.branch) set('branch', d.defaultBranch);
      } catch(e) { setRepoStatus({ error: e.message }); }
    }, 600);
    return () => clearTimeout(checkTimer.current);
  }, [selectedGhId, selectedAzId, cfg.repo, cfg.project, provider, backendDomain]);

  const handlePush = async () => {
    setPushing(true); setPushLogs([]); setDone(null);
    const resolvedMode = mode === 'auto' ? (repoStatus?.exists ? 'push' : 'create') : mode;
    const commitMessage = cfg.commitMessage || defaultMsg;
    const config = provider==='github'
      ? { token:ghAccount?.gitPat, owner:ghAccount?.gitUsername, repo:cfg.repo, branch:cfg.branch||'main', commitMessage, mode:resolvedMode, visibility:cfg.visibility, description:cfg.description }
      : { token:azAccount?.azDevopsPat, org:azAccount?.organization, project:cfg.project, repo:cfg.repo, branch:cfg.branch||'main', commitMessage, mode:resolvedMode, description:cfg.description };
    const filesList = Object.entries(files).map(([path,content])=>({path,content}));
    addPushLog('action', resolvedMode==='create' ? `Creating repo then pushing ${filesList.length} files…` : `Pushing ${filesList.length} files to existing repo…`);
    try {
      const resp = await fetch(`${backendDomain}/api/v1/migration/mule-push`, {
        method:'POST', headers:{'Content-Type':'application/json', ...authHeader()},
        body: JSON.stringify({ provider, config, files: filesList })
      });
      if (!resp.ok) throw new Error(`Backend error ${resp.status}`);
      const reader=resp.body.getReader(), decoder=new TextDecoder();
      let buffer='';
      while(true){
        const {done:d,value}=await reader.read();
        if(d) break;
        buffer+=decoder.decode(value,{stream:true});
        const blocks=buffer.split('\n\n'); buffer=blocks.pop();
        for(const block of blocks){
          let eventType='message', dataStr='';
          for(const line of block.split('\n')){
            if(line.startsWith('event: ')) eventType=line.slice(7).trim();
            else if(line.startsWith('data: ')) dataStr=line.slice(6);
          }
          if(!dataStr) continue;
          try{
            const ev=JSON.parse(dataStr);
            if(eventType==='status') addPushLog('info', ev.msg);
            else if(eventType==='repo_created') addPushLog('success', ev.msg);
            else if(eventType==='file_pushed') addPushLog('file',`✓ ${ev.path} (${ev.pushed}/${ev.total})`);
            else if(eventType==='file_error') addPushLog('error',`✕ ${ev.path}: ${ev.error}`);
            else if(eventType==='done'){ addPushLog('success', ev.msg); setDone({url:ev.url,pushed:ev.pushed,failed:ev.failed}); }
            else if(eventType==='error') addPushLog('error', ev.msg);
          }catch{}
        }
      }
    }catch(e){ addPushLog('error',e.message); }
    finally{ setPushing(false); }
  };

  const resolvedMode = mode==='auto' ? (repoStatus?.exists?'push':'create') : mode;
  const logColors = {action:'#93c5fd',success:'#6ee7b7',error:'#fca5a5',info:'#8b9ab0',file:'#86efac'};
  const isValid = canCheck && repoStatus && !repoStatus.checking && !repoStatus.error;

  const inp = (val, onChange, placeholder, type='text') => (
    <input type={type} value={val} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
      style={{width:'100%',background:'#080c12',border:'1px solid rgba(99,120,150,0.25)',borderRadius:7,
        padding:'7px 10px',color:'#f0f4f8',fontSize:12,outline:'none',boxSizing:'border-box'}} />
  );
  const sel = (val, onChange, opts) => (
    <select value={val} onChange={e=>onChange(e.target.value)}
      style={{width:'100%',background:'#080c12',border:'1px solid rgba(99,120,150,0.25)',borderRadius:7,
        padding:'7px 10px',color:'#f0f4f8',fontSize:12,outline:'none',boxSizing:'border-box'}}>
      {opts.map(([v,l])=><option key={v} value={v}>{l}</option>)}
    </select>
  );
  const field = (label, children) => (
    <div>
      <div style={{fontSize:10,color:'rgba(99,120,150,0.6)',fontFamily:'monospace',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>{label}</div>
      {children}
    </div>
  );

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.78)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9000}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{width:560,maxHeight:'90vh',background:'#0d1117',border:'1px solid rgba(99,120,150,0.25)',borderRadius:12,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',borderBottom:'1px solid rgba(99,120,150,0.15)',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:15}}>⬆</span>
            <span style={{color:'#f0f4f8',fontSize:13,fontWeight:600}}>Push to Repository</span>
            <span style={{color:'rgba(99,120,150,0.6)',fontSize:11,fontFamily:'monospace'}}>{fileCount} files</span>
          </div>
          <button onClick={onClose} style={{color:'rgba(99,120,150,0.6)',fontSize:16,background:'none',border:'none',cursor:'pointer'}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'14px 16px',display:'flex',flexDirection:'column',gap:12}}>
          <div style={{display:'flex',background:'#080c12',border:'1px solid rgba(99,120,150,0.15)',borderRadius:8,overflow:'hidden'}}>
            {[['github','⬡  GitHub'],['azure','⬡  Azure DevOps']].map(([id,label])=>(
              <button key={id} onClick={()=>{setProvider(id);setRepoStatus(null);setMode('auto');setCfg(p=>({...p,repo:'',project:'',branch:'main',commitMessage:''}));}}
                style={{flex:1,padding:'9px',background:provider===id?'#1e2530':'transparent',border:'none',
                  borderBottom:provider===id?'2px solid #3b82f6':'2px solid transparent',
                  color:provider===id?'#f0f4f8':'rgba(99,120,150,0.6)',fontSize:12,fontWeight:500,cursor:'pointer'}}>
                {label}
              </button>
            ))}
          </div>
          {provider==='github' && (
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {field('GitHub Account', sel(selectedGhId, v=>setSelectedGhId(v),
                [['','— Select account —'], ...githubAccounts.map(a=>[a._id, a.gitUsername])]
              ))}
              {githubAccounts.length===0 && <div style={{fontSize:11,color:'#f59e0b',fontFamily:'monospace'}}>⚠ No GitHub accounts found. Add one in Connected Apps.</div>}
              {field('Repository', inp(cfg.repo,v=>set('repo',v),'e.g. my-dotnet-api'))}
            </div>
          )}
          {provider==='azure' && (
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {field('Azure DevOps Account', sel(selectedAzId, v=>setSelectedAzId(v),
                [['','— Select account —'], ...azureAccounts.map(a=>[a._id, a.organization])]
              ))}
              {azureAccounts.length===0 && <div style={{fontSize:11,color:'#f59e0b',fontFamily:'monospace'}}>⚠ No Azure accounts found. Add one in Connected Apps.</div>}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                {field('Project', inp(cfg.project,v=>set('project',v),'e.g. IntegrationOS'))}
                {field('Repository', inp(cfg.repo,v=>set('repo',v),'e.g. my-dotnet-api'))}
              </div>
            </div>
          )}
          {canCheck && (
            <div style={{background:'#080c12',border:'1px solid rgba(99,120,150,0.15)',borderRadius:8,padding:'10px 12px',display:'flex',flexDirection:'column',gap:8}}>
              {repoStatus?.checking && <div style={{fontSize:11,color:'rgba(99,120,150,0.6)',fontFamily:'monospace',display:'flex',alignItems:'center',gap:6}}><Spinner/> Checking repo…</div>}
              {repoStatus?.error && <div style={{fontSize:11,color:'#f59e0b',fontFamily:'monospace'}}>⚠ {repoStatus.error}</div>}
              {repoStatus?.exists && <div style={{fontSize:11,fontFamily:'monospace',color:'#10b981'}}>✓ Repo exists · {repoStatus.private?'private':'public'} · default: {repoStatus.defaultBranch}</div>}
              {repoStatus?.exists===false && <div style={{fontSize:11,color:'#f59e0b',fontFamily:'monospace'}}>○ Repo not found — will be created</div>}
              {repoStatus && !repoStatus.checking && (
                <div style={{display:'flex',gap:6}}>
                  {[['push','↑ Push to existing'],['create',repoStatus?.exists?'+ Create new repo':'+ Create & push']].map(([m,label])=>(
                    <button key={m} onClick={()=>setMode(m)}
                      style={{flex:1,padding:'7px',borderRadius:7,fontSize:11,fontWeight:500,cursor:'pointer',
                        border:resolvedMode===m?'1px solid #3b82f6':'1px solid rgba(99,120,150,0.25)',
                        background:resolvedMode===m?'rgba(59,130,246,0.1)':'transparent',
                        color:resolvedMode===m?'#3b82f6':'rgba(99,120,150,0.6)'}}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {resolvedMode==='create' && repoStatus && !repoStatus.checking && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              {field('Visibility', sel(cfg.visibility, v=>set('visibility',v), [['private','Private'],['public','Public']]))}
              {field('Description', inp(cfg.description,v=>set('description',v),'Short description…'))}
            </div>
          )}
          <div style={{display:'grid',gridTemplateColumns:'130px 1fr',gap:10}}>
            {field('Branch', inp(cfg.branch,v=>set('branch',v),'main'))}
            {field('Commit message', inp(cfg.commitMessage,v=>set('commitMessage',v),defaultMsg))}
          </div>
          {pushLogs.length>0 && (
            <div style={{background:'#080c12',border:'1px solid rgba(99,120,150,0.15)',borderRadius:7,padding:'8px 10px',maxHeight:170,overflowY:'auto',fontFamily:'monospace',fontSize:11,lineHeight:1.6}}>
              {pushLogs.map((l,i)=>(
                <div key={i} style={{color:logColors[l.level]||'#8b9ab0'}}>
                  <span style={{color:'rgba(99,120,150,0.6)',marginRight:6}}>{l.time}</span>{l.msg}
                </div>
              ))}
              <div ref={logEndRef}/>
            </div>
          )}
          {done && (
            <div style={{background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:8,padding:'12px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10}}>
              <div>
                <div style={{color:'#10b981',fontSize:13,fontWeight:600,marginBottom:2}}>✓ {done.pushed} files pushed{done.failed>0?`, ${done.failed} failed`:''}</div>
                <div style={{color:'rgba(99,120,150,0.6)',fontSize:11,fontFamily:'monospace',wordBreak:'break-all'}}>{done.url}</div>
              </div>
              <a href={done.url} target="_blank" rel="noopener noreferrer"
                style={{background:'#10b981',color:'#000',borderRadius:6,padding:'6px 14px',fontSize:11,fontWeight:600,textDecoration:'none',whiteSpace:'nowrap',flexShrink:0}}>
                Open Repo ↗
              </a>
            </div>
          )}
        </div>
        <div style={{padding:'10px 16px',borderTop:'1px solid rgba(99,120,150,0.15)',display:'flex',gap:8,justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
          <div style={{fontSize:11,color:'rgba(99,120,150,0.6)',fontFamily:'monospace'}}>{resolvedMode==='create'?'+ Creates repo then pushes all files':'↑ Pushes to existing repo'}</div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={onClose} style={{padding:'6px 16px',borderRadius:7,border:'1px solid rgba(99,120,150,0.25)',background:'transparent',color:'#8b9ab0',fontSize:12,cursor:'pointer'}}>Cancel</button>
            <button onClick={handlePush} disabled={pushing||!canCheck||!repoStatus||repoStatus.checking||!!repoStatus.error}
              style={{padding:'6px 18px',borderRadius:7,border:'none',fontSize:12,fontWeight:600,display:'flex',alignItems:'center',gap:6,
                background:pushing||!canCheck||!repoStatus||repoStatus.checking||repoStatus.error?'#252d3a':'#3b82f6',
                color:pushing||!canCheck||!repoStatus||repoStatus.checking||repoStatus.error?'#4a5568':'#fff',
                cursor:pushing||!canCheck||!repoStatus||repoStatus.checking||repoStatus.error?'not-allowed':'pointer'}}>
              {pushing?<><Spinner/> Pushing…</>:resolvedMode==='create'?`Create & push to ${provider==='github'?'GitHub':'Azure'}`:`Push to ${provider==='github'?'GitHub':'Azure DevOps'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main MigrationTab ─────────────────────────────────────────────────────────
export default function MigrationTab({ backendDomain, processUrl, onNewSession, usageData, liveTokens, extensionStatus, onExtend, onUsageRefresh, canDotnet = true, canJitterbit = true }) {
  // Jitterbit multipart upload must hit intelog-be (:8083) DIRECTLY — the controller (:8081) proxy's
  // body-parsers consume the multipart body and strip the file. Fall back to backendDomain if not provided.
  const jbBase = processUrl || backendDomain;
  // Which migration types this user is allowed to use (defaults true for safety).
  const MIGRATION_TYPES = [
    canDotnet    && { id: 'dotnet',    label: '.NET',      title: 'MuleSoft → .NET' },
    canJitterbit && { id: 'jitterbit', label: 'Jitterbit', title: 'Jitterbit → MuleSoft' },
  ].filter(Boolean);
  const userId      = (() => { try { return JSON.parse(localStorage.getItem('user')||'{}').id||'anon'; } catch { return 'anon'; } })();
  const STORAGE_KEY = `migration_tab_state_${userId}`;
  const _persisted  = (() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)||'{}'); } catch { return {}; } })();

  const [uploadedFiles, setUploadedFiles] = useState(_persisted.uploadedFiles || {});
  const [uploadedName, setUploadedName]   = useState(_persisted.uploadedName  || '');
  const [extracting, setExtracting]       = useState(false);

  const _MG_STALE_THRESHOLD_MS = 6 * 60 * 60 * 1000;
  const [showStaleBanner, setShowStaleBanner] = useState(Boolean(
    _persisted.lastActive &&
    (Date.now() - _persisted.lastActive > _MG_STALE_THRESHOLD_MS) &&
    _persisted.messages &&
    _persisted.messages.length > 0
  ));

  const [messages, setMessages]     = useState(_persisted.messages   || []);
  const [input, setInput]           = useState('');
  const [streaming, setStreaming]   = useState(false);
  const [streamText, setStreamText] = useState('');
  const [thinkSteps, setThinkSteps] = useState(_persisted.thinkSteps || []);

  const [generatedFiles, setGeneratedFiles] = useState(_persisted.generatedFiles || {});
  const [selectedFile, setSelectedFile]     = useState(_persisted.selectedFile   || null);
  const [freshFiles, setFreshFiles]         = useState(new Set());
  const [panel, setPanel]                   = useState(_persisted.panel || 'source');

  const [logs, setLogs]         = useState(_persisted.logs    || []);
  const [showPush, setShowPush] = useState(false);
  const [sourceMode, setSourceMode] = useState('upload'); // 'upload' | 'git'
  const [rightTab, setRightTab]         = useState('viewer'); // 'viewer' | 'logs'
  const [rightExpanded, setRightExpanded] = useState(false);

  // Migration type: 'dotnet' (MuleSoft→.NET, SSE) | 'jitterbit' (Jitterbit→MuleSoft, /start + poll).
  // Only allow a persisted type the user still has permission for; else fall back to the first allowed.
  const _allowedTypeIds = [canDotnet && 'dotnet', canJitterbit && 'jitterbit'].filter(Boolean);
  const [migrationType, setMigrationType] = useState(
    _allowedTypeIds.includes(_persisted.migrationType) ? _persisted.migrationType : (_allowedTypeIds[0] || 'dotnet')
  );
  const [jbStatus, setJbStatus] = useState(null); // { status, progress, processed, total, results, errors }
  const [migratedBlob, setMigratedBlob] = useState(null); // Jitterbit output zip blob for direct download
  const jbPollRef = useRef(null);
  const lastJbStepRef = useRef(null); // dedupe repeated progress steps
  const rawFilesRef = useRef([]); // original uploaded File objects (needed for Jitterbit multipart /start)

  const chatEnd = useRef();
  const textarea = useRef();
  const streamAbortRef = useRef(null);
  const [sidebarPortal, setSidebarPortal] = useState(null);
  const [sourceCollapsed, setSourceCollapsed] = useState(false);
  useEffect(() => { setSidebarPortal(document.getElementById('mg-sidebar-portal')); }, []);
  // Auto-collapse source panel once files are loaded so file tree gets space
  useEffect(() => { if (Object.keys(uploadedFiles).length > 0) setSourceCollapsed(true); }, [uploadedFiles]);
  useEffect(() => { if (chatEnd.current) chatEnd.current.scrollIntoView({behavior:'smooth'}); }, [messages, streamText, thinkSteps]);

  // Persist session state to localStorage on every relevant change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        messages, uploadedName, uploadedFiles, generatedFiles,
        selectedFile, panel, logs, thinkSteps, migrationType,
        lastActive: Date.now(),
      }));
    } catch (_) {}
  }, [messages, uploadedName, uploadedFiles, generatedFiles, selectedFile, panel, logs, thinkSteps, migrationType]); // eslint-disable-line

  // Stop any in-flight Jitterbit poll on unmount
  useEffect(() => () => { if (jbPollRef.current) { clearInterval(jbPollRef.current); jbPollRef.current = null; } }, []);

  // Stable migration session id (reused across the /start → /status → /download-all calls)
  const getMigrationSessionId = useCallback(() => {
    let id = localStorage.getItem('migration_session_id');
    if (!id) {
      id = 'mg-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('migration_session_id', id);
    }
    return id;
  }, []);

  const handleNewSession = useCallback(() => {
    if (streamAbortRef.current) { streamAbortRef.current.abort(); streamAbortRef.current = null; }
    if (jbPollRef.current) { clearInterval(jbPollRef.current); jbPollRef.current = null; }
    rawFilesRef.current = [];
    setJbStatus(null);
    setMigratedBlob(null);
    setMigrationType(canDotnet ? 'dotnet' : (canJitterbit ? 'jitterbit' : 'dotnet'));
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('migration_session_id');
    if (onNewSession) onNewSession();
  }, [STORAGE_KEY, onNewSession, canDotnet, canJitterbit]);

  const log = useCallback((level, msg) => setLogs(p=>[...p,{time:ts(),level,msg}]), []);

  const handleUpload = useCallback(async files => {
    setExtracting(true);
    log('action',`Upload: ${files.map(f=>f.name).join(', ')}`);
    rawFilesRef.current = files; // keep the raw File objects for the Jitterbit multipart /start call
    const extracted = {};
    const textExts = ['.xml','.json','.yaml','.yml','.properties','.dwl','.raml','.wsdl','.xsd','.mf','.txt','.md','.js','.ts'];
    for (const file of files) {
      log('info',`Reading ${file.name} (${(file.size/1024).toFixed(1)} KB)`);
      try {
        const buf = await file.arrayBuffer();
        const zip = await JSZip.loadAsync(buf);
        let loaded=0, skipped=0;
        for (const [path,entry] of Object.entries(zip.files)) {
          if (entry.dir) continue;
          if (!textExts.some(e=>path.toLowerCase().endsWith(e))) { skipped++; continue; }
          try { extracted[path]=await entry.async('string'); log('file',`Extracted: ${path}`); loaded++; }
          catch { skipped++; }
        }
        log('success',`Done — ${loaded} files, ${skipped} binary skipped`);
        setUploadedName(file.name.replace(/\.(zip|jar)$/i,''));
      } catch(e) { log('error',`Failed: ${e.message}`); }
    }
    setUploadedFiles(extracted);
    setExtracting(false);
    const coreFiles = Object.entries(extracted).filter(([p])=>isMuleCore(p));
    log('info',`Core files detected: ${coreFiles.length}`);
    if (coreFiles.length) {
      setInput(`Migrate project: ${files[0].name} (${coreFiles.length} core files, ${Object.keys(extracted).length} total)`);
      log('action',`Ready — press Migrate to start`);
    }
  }, [log]);

  const downloadZip = useCallback(async () => {
    if (!Object.keys(generatedFiles).length) return;
    log('action','Packaging output…');
    const zip = new JSZip();
    const name = (uploadedName||'migrated')+'-dotnet';
    const folder = zip.folder(name);
    for (const [path,content] of Object.entries(generatedFiles)) folder.file(path,content);
    const blob = await zip.generateAsync({type:'blob'});
    log('success',`ZIP ready: ${name}.zip (${(blob.size/1024).toFixed(1)} KB)`);
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'),{href:url,download:name+'.zip'}).click();
    URL.revokeObjectURL(url);
  }, [generatedFiles, uploadedName, log]);

  const addStep = useCallback(text => setThinkSteps(p=>{
    const last=p[p.length-1];
    if (last&&!last.done) return [...p.slice(0,-1),{...last,done:true},{text,done:false}];
    return [...p,{text,done:false}];
  }), []);
  const doneSteps = useCallback(()=>setThinkSteps(p=>p.map(s=>({...s,done:true}))), []);

  // Download the Jitterbit→Mule output archive directly (blob captured from /download-all)
  const downloadMuleZip = useCallback(() => {
    if (!migratedBlob) return;
    const name = (uploadedName||'migrated')+'-mule';
    const url = URL.createObjectURL(migratedBlob);
    Object.assign(document.createElement('a'),{href:url,download:name+'.zip'}).click();
    URL.revokeObjectURL(url);
    log('success',`Downloaded ${name}.zip`);
  }, [migratedBlob, uploadedName, log]);

  // ── Jitterbit → MuleSoft migration (legacy /start multipart + poll + download-all) ──
  const runJitterbitMigration = useCallback(async () => {
    const rawFiles = rawFilesRef.current || [];
    if (!rawFiles.length) { log('error','Upload a Jitterbit .zip first'); return; }
    if (streaming) return;

    const abort = new AbortController();
    streamAbortRef.current = abort;
    const aborted = () => abort.signal.aborted;
    const sessionId = getMigrationSessionId();
    const t0 = Date.now();

    const projName = uploadedName || 'jitterbit-migration';
    setMessages(p=>[...p,{role:'user',content:`Migrate Jitterbit project: ${projName}`,id:Date.now()}]);
    setStreaming(true); setStreamText(''); setThinkSteps([]); setJbStatus(null); setMigratedBlob(null);
    lastJbStepRef.current = null;
    addStep('Uploading project & starting migration…');
    log('action',`Jitterbit → MuleSoft — ${rawFiles.length} file(s)`);

    try {
      const fd = new FormData();
      rawFiles.forEach(f => fd.append('files', f, f.name));
      fd.append('projectName', projName);
      fd.append('sourceType', 'jitterbit');
      fd.append('targetType', 'mule');
      fd.append('migrationConfig', JSON.stringify({
        strategy: 'standard',
        outputFormat: 'xml',
        optimizations: { removeUnusedComponents:true, optimizeFlows:false, generateDocumentation:true, validateConnectors:true },
        advanced: { parallelProcessing:false, maxRetries:3, customTemplates:false, preserveComments:true },
      }));

      const resp = await fetch(`${jbBase}/api/v1/migration/start`, {
        method:'POST', body: fd,
        headers: { 'x-session-id': sessionId, ...authHeader() }, // no Content-Type — browser sets the multipart boundary
        signal: abort.signal,
      });
      if (!resp.ok) throw new Error(`Backend ${resp.status}: ${await resp.text()}`);
      const started = await resp.json();
      if (!started.success) throw new Error(started.message || started.error || 'Migration failed to start');
      const migrationId = started.migrationId || started.sessionId;
      if (!migrationId) throw new Error('No migration ID returned by server');
      log('success',`Migration started (id: ${migrationId})`);
      addStep('Analyzing Jitterbit project…');

      // Poll status
      await new Promise((resolve) => {
        let polls = 0;
        const MAX_POLLS = 480; // ~20 min at 2.5s
        jbPollRef.current = setInterval(async () => {
          if (aborted()) { clearInterval(jbPollRef.current); jbPollRef.current=null; resolve(); return; }
          if (++polls > MAX_POLLS) {
            clearInterval(jbPollRef.current); jbPollRef.current=null;
            doneSteps(); log('error','Migration timed out (20 min). Please try again.');
            setMessages(p=>[...p,{role:'assistant',content:'Migration timed out.',id:Date.now()+1,parts:[{type:'text',content:'✕ Migration timed out after 20 minutes.'}]}]);
            resolve(); return;
          }
          try {
            const r = await fetch(`${jbBase}/api/v1/migration/${migrationId}/status`, { headers: authHeader() });
            if (!r.ok) throw new Error(`status ${r.status}`);
            const d = await r.json();
            if (!d.success) throw new Error(d.error || 'status failed');
            setJbStatus({ status:d.status, progress:d.progress||0, processed:d.processedFiles||0, total:d.totalFiles||0, results:d.results||[], errors:d.errors||[] });
            // Only add a think-step when the percentage actually changes (avoid flooding the box
            // with the same "processing — 33%" line on every poll). Live % is also shown in the bar.
            const stepMsg = `${d.status} — ${d.progress||0}%`;
            if (stepMsg !== lastJbStepRef.current) { lastJbStepRef.current = stepMsg; addStep(stepMsg); }

            if (d.status === 'completed' || d.status === 'failed') {
              clearInterval(jbPollRef.current); jbPollRef.current=null;
              if (d.status === 'failed') {
                const msg = (d.errors||[]).map(e=>e.error||e.message||e.general).filter(Boolean).join('; ') || 'Migration failed';
                doneSteps(); log('error',`Migration failed: ${msg}`);
                setMessages(p=>[...p,{role:'assistant',content:`Error: ${msg}`,id:Date.now()+1,parts:[{type:'text',content:`✕ ${msg}`}]}]);
                resolve(); return;
              }
              // completed → fetch + unzip the Mule project
              log('success',`Migration complete — packaging MuleSoft project…`);
              addStep('Downloading generated MuleSoft project…');
              try {
                const dl = await fetch(`${jbBase}/api/v1/migration/${migrationId}/download-all`, { headers: authHeader() });
                if (!dl.ok) throw new Error(`download ${dl.status}`);
                const blob = await dl.blob();
                setMigratedBlob(blob);
                const zip = await JSZip.loadAsync(blob);
                const out = {};
                for (const [path, entry] of Object.entries(zip.files)) {
                  if (entry.dir) continue;
                  try { out[path] = await entry.async('string'); } catch { /* skip binary */ }
                }
                const names = Object.keys(out);
                setGeneratedFiles(out);
                setFreshFiles(new Set(names));
                setTimeout(()=>setFreshFiles(new Set()),3000);
                // Land on a real Mule flow file if present, else the first file.
                const firstFlow = names.find(n => /src\/main\/mule\/.*\.xml$/i.test(n)) || names[0];
                if (firstFlow) setSelectedFile(firstFlow);
                setPanel('output');
                doneSteps();
                log('success',`${names.length} MuleSoft file(s) generated — ${((Date.now()-t0)/1000).toFixed(1)}s`);
                setMessages(p=>[...p,{role:'assistant',content:`✓ Migrated to MuleSoft — ${names.length} files generated.`,id:Date.now()+1,fileCount:names.length,target:'MuleSoft',parts:[{type:'text',content:`✓ Migrated to MuleSoft — **${names.length}** files generated. See the output tree, or download the project.`}]}]);
              } catch(e) {
                doneSteps(); log('error',`Could not fetch output: ${e.message}`);
                setMessages(p=>[...p,{role:'assistant',content:`Migration completed but the output couldn't be retrieved: ${e.message}`,id:Date.now()+1,parts:[{type:'text',content:`⚠ Migration completed but output retrieval failed: ${e.message}`}]}]);
              }
              resolve(); return;
            }
          } catch(e) {
            clearInterval(jbPollRef.current); jbPollRef.current=null;
            if (!aborted()) { doneSteps(); log('error',`Polling error: ${e.message}`); }
            resolve();
          }
        }, 2500);
      });
    } catch(e) {
      if (!aborted()) {
        doneSteps(); log('error',`Failed: ${e.message}`);
        setMessages(p=>[...p,{role:'assistant',content:`Error: ${e.message}`,id:Date.now()+1,parts:[{type:'text',content:`✕ ${e.message}`}]}]);
      }
    } finally {
      if (!aborted()) { setStreaming(false); setStreamText(''); log('info','Agent idle'); }
      // Refresh the token-usage panel with the user's current daily totals (matches brownfield).
      if (onUsageRefresh) onUsageRefresh();
    }
  }, [streaming, jbBase, uploadedName, getMigrationSessionId, log, addStep, doneSteps, onUsageRefresh]);

  const send = useCallback(async text => {
    if (!text.trim()||streaming) return;

    // Jitterbit: the migrate action is the explicit "Migrate to MuleSoft" button; chat review is .NET-only.
    if (migrationType === 'jitterbit') {
      const t = text.toLowerCase();
      if (isMigrationIntent(t) || t.includes('migrate')) { setInput(''); runJitterbitMigration(); return; }
      setMessages(p=>[...p,
        {role:'user',content:text,id:Date.now()},
        {role:'assistant',content:'Chat review is available for .NET migrations. For Jitterbit, upload the project and click “Migrate to MuleSoft”.',id:Date.now()+1,parts:[{type:'text',content:'Chat review is available for **.NET** migrations. For Jitterbit, upload the project and click **⚡ Migrate to MuleSoft**.'}]},
      ]);
      setInput('');
      return;
    }

    const abort = new AbortController();
    streamAbortRef.current = abort;
    const aborted = () => abort.signal.aborted;

    const userMsg = {role:'user',content:text,id:Date.now()};
    setMessages(p=>[...p,userMsg]);
    setInput(''); setStreaming(true); setStreamText(''); setThinkSteps([]);
    const t0 = Date.now();
    const wantsMigration = isMigrationIntent(text) && Object.keys(uploadedFiles).length > 0;

    if (!wantsMigration) {
      log('info','Chat mode — routing to /api/chat');
      const history = [...messages, userMsg].map(m=>({role:m.role,content:m.content}));
      try {
        const resp = await fetch(`${backendDomain}/api/v1/migration/mule-chat`, {
          method:'POST', headers:{'Content-Type':'application/json', ...authHeader()},
          body: JSON.stringify({ messages: history, system: CHAT_SYSTEM_PROMPT }),
          signal: abort.signal,
        });
        if (!resp.ok) throw new Error(`Backend ${resp.status}: ${await resp.text()}`);
        const reader=resp.body.getReader(), decoder=new TextDecoder();
        let buffer='', fullText='';
        while (true) {
          const {done,value}=await reader.read();
          if (done || aborted()) break;
          buffer += decoder.decode(value,{stream:true});
          const blocks=buffer.split('\n\n'); buffer=blocks.pop();
          for (const block of blocks) {
            let eventType='message', dataStr='';
            for (const line of block.split('\n')) {
              if (line.startsWith('event: ')) eventType=line.slice(7).trim();
              else if (line.startsWith('data: ')) dataStr=line.slice(6);
            }
            if (!dataStr) continue;
            try {
              const ev=JSON.parse(dataStr);
              if (eventType==='delta') { fullText+=ev.text; setStreamText(fullText); }
              else if (eventType==='done') log('success',`in:${ev.inputTokens} out:${ev.outputTokens} tokens — ${((Date.now()-t0)/1000).toFixed(1)}s`);
              else if (eventType==='error') log('error', ev.msg);
            } catch {}
          }
        }
        if (aborted()) return;
        doneSteps(); setStreamText('');
        const files=parseFiles(fullText), fileNames=Object.keys(files);
        if (fileNames.length) {
          setGeneratedFiles(p=>({...p,...files}));
          setFreshFiles(new Set(fileNames));
          setTimeout(()=>setFreshFiles(new Set()),3000);
          if (!selectedFile) setSelectedFile(fileNames[0]);
          setPanel('output');
          log('success',`${fileNames.length} file(s) updated`);
        }
        setMessages(p=>[...p,{role:'assistant',content:fullText,id:Date.now()+1,parts:parseParts(fullText),fileCount:fileNames.length}]);
      } catch(e) {
        if (aborted()) return;
        doneSteps(); log('error',`Chat failed: ${e.message}`);
        setMessages(p=>[...p,{role:'assistant',content:`Error: ${e.message}`,id:Date.now()+1,parts:[{type:'text',content:`✕ ${e.message}`}]}]);
        setStreamText('');
      } finally { if (!aborted()) { setStreaming(false); log('info','Agent idle'); } }
      return;
    }

    // Full migration pipeline
    const coreEntries = Object.entries(uploadedFiles).filter(([p])=>isMuleCore(p));
    const allEntries  = Object.entries(uploadedFiles);
    const filesList   = (coreEntries.length>0?coreEntries:allEntries).map(([path,content])=>({path,content}));
    const totalChars  = filesList.reduce((s,f)=>s+f.content.length,0);
    log('action',`Migration mode — ${filesList.length} files (~${Math.round(totalChars/1000)}k chars)`);
    addStep('Starting migration pipeline…');
    try {
      const resp = await fetch(`${backendDomain}/api/v1/migration/mule-dotnet`, {
        method:'POST', headers:{'Content-Type':'application/json', ...authHeader()},
        body: JSON.stringify({files:filesList, projectHint:text, system:SYSTEM_PROMPT}),
        signal: abort.signal,
      });
      if (!resp.ok) throw new Error(`Backend ${resp.status}: ${await resp.text()}`);
      log('success',`SSE stream opened (${Date.now()-t0}ms)`);
      addStep('Agent analyzing project…');
      const reader=resp.body.getReader(), decoder=new TextDecoder();
      let buffer='', fullText='', fileCount=0;
      while (true) {
        const {done,value}=await reader.read();
        if (done || aborted()) break;
        buffer += decoder.decode(value,{stream:true});
        const blocks=buffer.split('\n\n'); buffer=blocks.pop();
        for (const block of blocks) {
          let eventType='message', dataStr='';
          for (const line of block.split('\n')) {
            if (line.startsWith('event: ')) eventType=line.slice(7).trim();
            else if (line.startsWith('data: ')) dataStr=line.slice(6);
          }
          if (!dataStr) continue;
          try {
            const ev=JSON.parse(dataStr);
            if (eventType==='delta') {
              fullText+=ev.text; setStreamText(fullText);
              const matches=[...fullText.matchAll(/```file:([^\n]+)/g)];
              if (matches.length!==fileCount) {
                fileCount=matches.length;
                const fname=matches[matches.length-1][1].trim();
                addStep(`Generating ${fname}`);
                log('file',`Generating: ${fname}`);
              }
            } else if (eventType==='status') {
              log('info', ev.msg);
            } else if (eventType==='phase') {
              log('action', ev.msg); addStep(ev.msg);
            } else if (eventType==='plan') {
              const p=ev.plan;
              log('ai',`Plan: ${p.projectName} — ${p.dotnetFiles?.length} files in ${p.batches?.length} batches`);
            } else if (eventType==='pass_done') {
              log('node', ev.msg);
              if (ev.truncated) { log('warn',`[${ev.label}] token limit — auto-continuing…`); addStep('Continuing (truncated)…'); }
            } else if (eventType==='batch_start') {
              log('node',`Batch ${ev.batch}/${ev.total}: ${(ev.files||[]).join(', ')}`);
              addStep(`Batch ${ev.batch}/${ev.total} — generating…`);
            } else if (eventType==='batch_done') {
              log('success',`Batch ${ev.batch}/${ev.total} complete`);
            } else if (eventType==='warn') {
              log('warn', ev.msg);
            } else if (eventType==='error') {
              log('error', ev.msg);
            } else if (eventType==='done') {
              log('success',`${ev.msg} — ${((Date.now()-t0)/1000).toFixed(1)}s`);
            }
          } catch {}
        }
      }
      if (aborted()) return;
      doneSteps(); setStreamText('');
      const files=parseFiles(fullText), fileNames=Object.keys(files);
      if (fileNames.length) {
        setGeneratedFiles(p=>({...p,...files}));
        setFreshFiles(new Set(fileNames));
        setTimeout(()=>setFreshFiles(new Set()),3000);
        if (!selectedFile) setSelectedFile(fileNames[0]);
        setPanel('output');
        log('success',`Migration complete — ${fileNames.length} .NET files`);
      } else { log('warn','No file blocks detected — check backend logs'); }
      setMessages(p=>[...p,{role:'assistant',content:fullText,id:Date.now()+1,parts:parseParts(fullText),fileCount:fileNames.length}]);
    } catch(e) {
      if (aborted()) return;
      doneSteps(); log('error',`Failed: ${e.message}`);
      setMessages(p=>[...p,{role:'assistant',content:`Error: ${e.message}`,id:Date.now()+1,parts:[{type:'text',content:`✕ ${e.message}`}]}]);
      setStreamText('');
    } finally {
      if (!aborted()) { setStreaming(false); log('info','Agent idle'); }
      if (onUsageRefresh) onUsageRefresh();  // refresh token-usage panel after each .NET migration / chat turn
    }
  }, [messages, streaming, log, addStep, doneSteps, selectedFile, uploadedFiles, backendDomain, migrationType, runJitterbitMigration, onUsageRefresh]);

  const handleKey = e => { if (e.key==='Enter'&&!e.shiftKey) { e.preventDefault(); send(input); } };

  const outputCount = Object.keys(generatedFiles).length;
  const sourceCount = Object.keys(uploadedFiles).length;
  const coreCount   = Object.keys(uploadedFiles).filter(p=>isMuleCore(p)).length;

  return (
    <div className="ma-migration-root">
      {/* Toolbar */}
      <div className="ma-topbar">
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          {/* Migration-type selector — only shows the types this user is permitted to use */}
          {MIGRATION_TYPES.length > 1 && (
            <div style={{display:'flex',background:'rgba(8,12,18,0.6)',border:'1px solid rgba(99,120,150,0.15)',borderRadius:8,overflow:'hidden'}}>
              {MIGRATION_TYPES.map(({id,label,title})=>(
                <button key={id} title={title}
                  onClick={()=>{ if(!streaming) setMigrationType(id); }}
                  disabled={streaming}
                  style={{padding:'5px 12px',background:migrationType===id?'rgba(59,130,246,0.14)':'transparent',border:'none',borderBottom:migrationType===id?'2px solid #3b82f6':'2px solid transparent',color:migrationType===id?'#f0f4f8':'rgba(99,120,150,0.6)',fontSize:11,fontWeight:600,cursor:streaming?'not-allowed':'pointer',fontFamily:'inherit'}}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <span style={{fontSize:12,fontWeight:600,color:'rgba(99,120,150,0.7)'}}>{migrationType==='jitterbit'?'Jitterbit → MuleSoft':'MuleSoft → .NET 8'}</span>
          {uploadedName && <span style={{fontSize:11,color:'rgba(99,120,150,0.6)',fontFamily:'monospace'}}>/ {uploadedName}</span>}
        </div>
        <div style={{display:'flex',gap:7,alignItems:'center'}}>
          {outputCount>0 && (
            <div style={{display:'flex',gap:6}}>
              <button className="ma-btn ma-btn-ghost" onClick={migrationType==='jitterbit'&&migratedBlob?downloadMuleZip:downloadZip} style={{padding:'4px 12px'}}>↓ .zip ({outputCount})</button>
              <button className="ma-btn ma-btn-primary" onClick={()=>setShowPush(true)} style={{padding:'4px 14px'}}>⬆ Push to Repo</button>
            </div>
          )}
        </div>
      </div>

      {/* Portal the left panel into the SmartHub sidebar */}
      {sidebarPortal && ReactDOM.createPortal(
        <div className="ma-migration-root" style={{display:'flex',flexDirection:'column',flex:1,overflow:'hidden',background:'transparent',borderTop:'1px solid rgba(99,120,150,0.15)',paddingTop:12}}>

          {/* Collapsible source section header */}
          <div
            onClick={()=>setSourceCollapsed(p=>!p)}
            style={{display:'flex',alignItems:'center',justifyContent:'space-between',cursor:'pointer',marginBottom:sourceCollapsed?6:10,userSelect:'none'}}
          >
            <div style={{fontSize:10,fontWeight:600,color:'rgba(99,120,150,0.5)',textTransform:'uppercase',letterSpacing:'0.08em',fontFamily:'monospace'}}>
              {sourceCollapsed && uploadedName ? uploadedName : 'Source Project'}
            </div>
            <span style={{fontSize:10,color:'rgba(99,120,150,0.4)',transition:'transform 0.2s',display:'inline-block',transform:sourceCollapsed?'rotate(0deg)':'rotate(180deg)'}}>▲</span>
          </div>

          {/* Collapsible source body */}
          {!sourceCollapsed && (
            <div style={{flexShrink:0}}>
              {/* Source mode toggle — Git fetch is .NET-only (Jitterbit needs the raw .zip for multipart upload) */}
              {migrationType !== 'jitterbit' && (
                <div style={{display:'flex',background:'rgba(8,12,18,0.6)',border:'1px solid rgba(99,120,150,0.15)',borderRadius:8,overflow:'hidden',marginBottom:10}}>
                  {[['upload','⬆ Upload .zip'],['git','⎇ From Git']].map(([m,label])=>(
                    <button key={m} onClick={e=>{e.stopPropagation();setSourceMode(m);}}
                      style={{flex:1,padding:'7px 4px',background:sourceMode===m?'rgba(59,130,246,0.12)':'transparent',border:'none',borderBottom:sourceMode===m?'2px solid #3b82f6':'2px solid transparent',color:sourceMode===m?'#f0f4f8':'rgba(99,120,150,0.55)',fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:'inherit'}}>
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {(migrationType==='jitterbit' || sourceMode==='upload') ? (
                <div style={{marginBottom:8}}>
                  <DropZone onFiles={handleUpload} disabled={extracting||streaming}
                    accept={migrationType==='jitterbit'?'.zip':'.zip,.jar'}
                    idleText={migrationType==='jitterbit'?'Drop Jitterbit .zip':'Drop MuleSoft .zip or .jar'} />
                </div>
              ) : (
                <GitSourcePanel
                  backendDomain={backendDomain}
                  disabled={streaming}
                  log={log}
                  onFiles={files => { setUploadedFiles(files); const cnt=Object.keys(files).length; log('success',`Fetched ${cnt} files from repo`); }}
                  onName={name => { setUploadedName(name); setInput(`Migrate project: ${name}`); }}
                />
              )}

              {migrationType==='jitterbit' && sourceCount>0 && (
                <button
                  onClick={()=>runJitterbitMigration()}
                  disabled={streaming}
                  style={{width:'100%',marginBottom:8,padding:'8px 14px',borderRadius:7,border:'none',fontSize:12,fontWeight:600,cursor:streaming?'not-allowed':'pointer',background:streaming?'#252d3a':'#3b82f6',color:streaming?'#4a5568':'#fff',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                  {streaming?<><Spinner/> Migrating…</>:'⚡ Migrate to MuleSoft'}
                </button>
              )}

              {extracting && (
                <div style={{display:'flex',alignItems:'center',gap:6,fontSize:11,color:'#3b82f6',fontFamily:'monospace',padding:'4px 0 6px'}}>
                  <Spinner /> Extracting…
                </div>
              )}
            </div>
          )}

          {/* Compact stats when collapsed */}
          {sourceCount > 0 && (
            <div style={{display:'flex',gap:5,marginBottom:8,flexShrink:0}}>
              {[['#10b981',coreCount,'Core'],['#3b82f6',sourceCount,'SRC'],['#f0f4f8',outputCount,migrationType==='jitterbit'?'Mule':'.NET']].map(([color,val,label])=>(
                <div key={label} style={{flex:1,background:'rgba(22,27,35,0.8)',border:'1px solid rgba(99,120,150,0.12)',borderRadius:6,padding:'4px 6px',textAlign:'center'}}>
                  <div style={{fontSize:13,fontWeight:600,fontFamily:'monospace',lineHeight:1,color}}>{val}</div>
                  <div style={{fontSize:8,color:'#4a5568',marginTop:2,textTransform:'uppercase',letterSpacing:'0.05em'}}>{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Files section */}
          <div style={{fontSize:10,fontWeight:600,color:'rgba(99,120,150,0.5)',textTransform:'uppercase',letterSpacing:'0.08em',fontFamily:'monospace',marginBottom:6,paddingLeft:2,flexShrink:0}}>
            Files
          </div>

          {/* File tree tabs */}
          <div style={{display:'flex',borderBottom:'1px solid rgba(99,120,150,0.15)',flexShrink:0,marginBottom:4}}>
            {['source','output'].map(p=>(
              <button key={p}
                onClick={()=>setPanel(p)}
                style={{flex:1,padding:'7px 4px',fontSize:10,fontWeight:500,letterSpacing:'0.03em',background:'none',border:'none',borderBottom:panel===p?'2px solid #3b82f6':'2px solid transparent',color:panel===p?'#f0f4f8':'#4a5568',cursor:'pointer',fontFamily:'inherit',transition:'all 0.15s'}}>
                {p==='source'?`SRC ${sourceCount||''}`:`${migrationType==='jitterbit'?'MULE':'.NET'} ${outputCount||''}`}
              </button>
            ))}
          </div>

          <div className="ma-tree-scroll" style={{flex:1,overflow:'auto'}}>
            {panel==='source'
              ? <FileTree files={uploadedFiles} selected={selectedFile} onSelect={p=>{setSelectedFile(p);setPanel('source');}} emptyText="Upload a project" />
              : <FileTree files={generatedFiles} selected={selectedFile} onSelect={p=>{setSelectedFile(p);setPanel('output');}} accent="#10b981" emptyText="Run migration" />}
          </div>
        </div>,
        sidebarPortal
      )}

      {/* Body */}
      <div style={{flex:1,display:'flex',overflow:'hidden'}}>

        {/* ── CENTER: Chat ── */}
        <div className="ma-chat-col">
          <div className="ma-messages">
            {showStaleBanner && (
              <StaleBanner
                lastActive={_persisted.lastActive}
                onNewSession={() => { handleNewSession(); setShowStaleBanner(false); }}
                onDismiss={() => setShowStaleBanner(false)}
              />
            )}
            {messages.map(msg=>(
              <div key={msg.id} className={`ma-msg ${msg.role==='user'?'ma-msg--user':'ma-msg--bot'}`}>
                <div className="ma-msg-avatar" style={msg.role==='user'?{background:'rgba(99,120,150,0.15)',border:'1px solid rgba(99,120,150,0.2)',fontSize:14}:{}}>
                  {msg.role==='user' ? '👤' : <img src={intelogLogo} alt="intelog" style={{width:'100%',height:'100%',objectFit:'contain',padding:4}} />}
                </div>
                <div className="ma-msg-body">
                  <div className="ma-msg-meta">
                    <span className="ma-msg-name">{msg.role==='user'?'You':'Migration Agent'}</span>
                    <span className="ma-msg-time">{msg.time||''}</span>
                  </div>
                  <div className="ma-msg-bubble">
                    {msg.role==='user' ? (
                      <span style={{whiteSpace:'pre-wrap'}}>{msg.content.length>180?msg.content.slice(0,180)+'…':msg.content}</span>
                    ) : (
                      <>
                        {msg.fileCount>0 && (
                          <div className="ma-file-badge">
                            <span style={{width:4,height:4,borderRadius:'50%',background:'#10b981',display:'inline-block'}} />
                            {msg.fileCount} {msg.target || '.NET'} files generated
                          </div>
                        )}
                        {(msg.parts||[{type:'text',content:msg.content}]).map((p,i)=>{
                          if (p.type==='text') return <Prose key={i} content={p.content} />;
                          if (p.type==='file') return <CodeBlock key={i} code={p.code} lang={p.lang} path={p.path} isNew={freshFiles.has(p.path)} onView={x=>{setSelectedFile(x);setPanel('output');}} />;
                          if (p.type==='code') return <CodeBlock key={i} code={p.code} lang={p.lang} />;
                          return null;
                        })}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {streaming&&(streamText||thinkSteps.length>0) && (
              <div className="ma-msg ma-msg--bot">
                <div className="ma-msg-avatar" style={{position:'relative'}}><img src={intelogLogo} alt="intelog" style={{width:'100%',height:'100%',objectFit:'contain',padding:4,opacity:0.6}} /><span style={{position:'absolute',bottom:0,right:0}}><Spinner/></span></div>
                <div className="ma-msg-body">
                  <div className="ma-msg-meta">
                    <span className="ma-msg-name">Migration Agent</span>
                  </div>
                  {thinkSteps.length>0 && (
                    <div className="ma-think-box">
                      {thinkSteps.map((s,i)=>(
                        <div key={i} className="ma-think-row">
                          <span className={`ma-think-icon${s.done?' ma-think-done':' ma-think-active'}`}>{s.done?'✓':<Spinner/>}</span>
                          <span className="ma-think-text" style={{color:s.done?'#10b981':'#8b9ab0'}}>{s.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {migrationType==='jitterbit' && jbStatus && (
                    <div style={{marginTop:8}}>
                      <div style={{height:6,background:'rgba(99,120,150,0.15)',borderRadius:4,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${Math.min(100,jbStatus.progress||0)}%`,background:'#3b82f6',transition:'width 0.3s'}} />
                      </div>
                      <div style={{fontSize:10,color:'rgba(99,120,150,0.7)',fontFamily:'monospace',marginTop:4}}>
                        {jbStatus.status} · {jbStatus.progress||0}%{jbStatus.processed>0 ? ` · ${jbStatus.processed}/${jbStatus.total||0} files` : ''}
                      </div>
                    </div>
                  )}
                  {streamText && (
                    <div className="ma-msg-bubble">
                      <Prose content={streamText.replace(/```file:[^\n]+\n[\s\S]*?```/g,'').slice(0,1200)+(streamText.length>1200?'\n\n…':'')} />
                    </div>
                  )}
                </div>
              </div>
            )}
            <div ref={chatEnd} />
          </div>

          <div className="ma-input-bar">
            <div className="ma-input-wrap">
              <textarea ref={textarea} className="ma-input-ta" value={input} onChange={e=>setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder={streaming?'Agent working…':migrationType==='jitterbit'?(sourceCount>0?'Type "migrate" or use the ⚡ Migrate to MuleSoft button…':'Upload a Jitterbit .zip to start…'):sourceCount>0?'Ask anything, or say "migrate this project" to start…':'Upload a project, then ask anything…'}
                disabled={streaming} rows={2} />
              <button className={`ma-send-btn${streaming||!input.trim()?'':' active'}`}
                onClick={()=>send(input)} disabled={streaming||!input.trim()}>
                {streaming?<Spinner/>:'↑'}
              </button>
            </div>
            <div className="ma-input-hint">Enter to send · Shift+Enter for newline</div>
          </div>
        </div>

        {/* ── RIGHT: Tabbed panel ── */}
        <div className={`ma-right-col${rightExpanded ? ' ma-right-col--expanded' : ''}`}>

          {/* Tab bar */}
          <div className="ma-right-tabbar">
            {[['viewer','File View'],['logs','Agent Logs']].map(([k,label])=>(
              <button key={k} className={`ma-right-tab${rightTab===k?' ma-right-tab--active':''}`} onClick={()=>setRightTab(k)}>
                {label}
                {k==='logs' && logs.length>0 && <span className="ma-right-tab-badge">{logs.length}</span>}
              </button>
            ))}
            <button className="ma-right-expand-btn" onClick={()=>setRightExpanded(p=>!p)} title={rightExpanded?'Collapse panel':'Expand panel'}>
              {rightExpanded ? '⇥' : '⇤'}
            </button>
          </div>

          {/* Tab content */}
          <div className="ma-right-content">
            {rightTab==='viewer' ? (
              selectedFile ? (() => {
                const src = panel==='source' ? uploadedFiles[selectedFile] : generatedFiles[selectedFile];
                if (!src) return (
                  <div className="ma-viewer-empty">
                    <div className="ma-viewer-empty-inner">
                      <div className="ma-viewer-empty-icon">◈</div>
                      <div className="ma-viewer-empty-text">Not in this panel</div>
                    </div>
                  </div>
                );
                const lang = fileLang(selectedFile);
                return (
                  <>
                    <div className="ma-viewer-header">
                      <div className="ma-viewer-path">
                        <span style={{fontSize:11,color:'rgba(99,120,150,0.6)',fontFamily:'monospace'}}>{fileIcon(selectedFile)}</span>
                        <span className="ma-viewer-filename">{selectedFile}</span>
                        <span className="ma-viewer-lines">{src.split('\n').length}L</span>
                        {panel==='output'&&freshFiles.has(selectedFile)&&<span className="ma-viewer-new">new</span>}
                        {panel==='source'&&<span className="ma-viewer-src">source</span>}
                      </div>
                      <button className="ma-btn ma-btn-ghost" style={{padding:'3px 10px',fontSize:10}} onClick={()=>navigator.clipboard.writeText(src)}>copy</button>
                    </div>
                    <div className="ma-viewer-body">
                      <pre style={{margin:0,fontFamily:'monospace',fontSize:12,lineHeight:1.7,color:'#abb2bf',whiteSpace:'pre-wrap'}}
                        dangerouslySetInnerHTML={{__html:hl(src,lang)}} />
                    </div>
                  </>
                );
              })() : (
                <div className="ma-viewer-empty">
                  <div className="ma-viewer-empty-inner">
                    <div className="ma-viewer-empty-icon">◈</div>
                    <div className="ma-viewer-empty-text">Select a file to view</div>
                    <div className="ma-viewer-empty-sub">SRC · Generated {migrationType==='jitterbit'?'MuleSoft':'.NET'}</div>
                  </div>
                </div>
              )
            ) : (
              <div className="sh-dashboard-col" style={{flex:1,minHeight:0,overflow:'hidden'}}>
                <SharedLogsPanel logs={logs} onClear={()=>setLogs([])} usageData={usageData} liveTokens={liveTokens} extensionStatus={extensionStatus} onExtend={onExtend} logsOnly />
              </div>
            )}
          </div>

          {/* Token usage — always visible footer */}
          <div style={{padding:'0 16px 16px'}}>
            <SharedLogsPanel tokenOnly usageData={usageData} liveTokens={liveTokens} extensionStatus={extensionStatus} onExtend={onExtend} />
          </div>

        </div>

      </div>

      {showPush && <PushPanel files={generatedFiles} uploadedName={uploadedName} onClose={()=>setShowPush(false)} backendDomain={backendDomain} targetLabel={migrationType==='jitterbit'?'MuleSoft':'.NET 8'} />}
    </div>
  );
}
